import React, { useEffect, useRef, useState } from 'react';
import { useSelector, useDispatch } from 'react-redux';
import { makeStyles } from '@material-ui/core/styles';
import ThemedDialog from 'components/ThemedDialog';
import { Link, useLocation, useHistory } from 'react-router-dom';
import _get from 'lodash/get';

import { setLeavingModal } from 'containers/Main/actions';
import {
  makeSelectClientDetails,
  makeSelectLeavingModal,
} from 'containers/Main/selectors';
import DialogContent from '@material-ui/core/DialogContent';
// Close button is handled by ThemedDialog using theme or default icon
import Typography from '@material-ui/core/Typography';
import Box from '@material-ui/core/Box';
import useSiteCopySelector from 'components/useSiteCopySelector';
import Button from '@material-ui/core/Button';
import Mixpanel from 'utils/mixpanelService';
import RichTextDescription from 'components/RichTextDescription';
import { isEmbedded, getParentPosition } from 'utils/embedded';
import useClientTheme from 'components/Hooks/useClientTheme';
import { useClientCustomLabelsAll } from 'components/Hooks/useClientCustomLabels';
import EmbeddedRedirectContent from './EmbeddedRedirectContent';

const useStyles = makeStyles(theme => ({
  paper: ({ leavingAppDialogOverridesStyles }) => ({
    borderRadius: '5px !important',
    [theme.breakpoints.up('800')]: {
      width: 750,
      maxWidth: 750,
    },
    [theme.breakpoints.down('799')]: {
      margin: '12px',
    },
    ..._get(leavingAppDialogOverridesStyles, 'paper', {}),
  }),
  header: ({ leavingAppDialogOverridesStyles }) => ({
    display: 'flex',
    justifyContent: 'flex-end',
    padding: '15px 15px 0px 0px',
    ..._get(leavingAppDialogOverridesStyles, 'header', {}),
  }),
  contentRoot: ({ leavingAppDialogOverridesStyles }) => ({
    margin: 0,
    padding: '10px 50px 50px',
    [theme.breakpoints.down('800')]: {
      padding: '10px 24px 30px',
    },
    '& h4': {
      padding: '0 55px',
      [theme.breakpoints.down('800')]: {
        padding: 0,
        fontSize: '18px',
      },
    },
    ..._get(leavingAppDialogOverridesStyles, 'contentRoot', {}),
  }),
  closeButton: ({ leavingAppDialogOverridesStyles }) => ({
    color: theme.palette.grey[500],
    padding: 0,
    '& span': {
      '& svg': {
        fontSize: 35,
        [theme.breakpoints.down('800')]: {
          fontSize: 26,
        },
      },
    },
    ..._get(leavingAppDialogOverridesStyles, 'closeButton', {}),
  }),
  button: ({ leavingAppDialogOverridesStyles }) => ({
    width: '50%',
    fontWeight: 600,
    marginTop: 30,
    padding: '20px 24px',
    borderRadius: 2,
    fontSize: 14,
    fontFamily: theme.typography.fontFamily || 'PoppinsRegular',
    [theme.breakpoints.down('800')]: {
      width: '100%',
      padding: '12px 16px',
      marginTop: '10px',
    },
    ..._get(leavingAppDialogOverridesStyles, 'button', {}),
  }),
  articleButton: ({ leavingAppDialogOverridesStyles }) => ({
    display: 'inline-flex',
    alignItems: 'center',
    justifyContent: 'center',
    width: '50%',
    border: 1,
    fontWeight: 600,
    borderColor: theme.palette.primary.main,
    background: '#ffff',
    marginTop: 30,
    borderRadius: 2,
    fontSize: 14,
    fontFamily: theme.typography.fontFamily || 'PoppinsRegular',
    textAlign: 'center',
    padding: '16px 24px',
    borderStyle: 'solid',
    color: theme.palette.primary.main,
    [theme.breakpoints.down('800')]: {
      width: '100%',
      alignItems: 'center',
      justifyContent: 'center',
      display: 'flex',
      padding: '9px 16px',
      marginTop: '10px',
    },
    '&:hover': {
      background: '#fff',
      color: theme.palette.primary.main,
    },
    ..._get(leavingAppDialogOverridesStyles, 'secondaryButton', {}),
  }),
  pageHeaderText: {
    color: '#01619c',
  },
  buttonSpace: ({ leavingAppDialogOverridesStyles }) => ({
    columnGap: 10,
    [theme.breakpoints.down('800')]: {
      flexDirection: 'column',
    },
    ..._get(leavingAppDialogOverridesStyles, 'buttonSpace', {}),
  }),
  disclaimerWrapper: {
    paddingTop: 20,
    '& p': {
      fontSize: '0.85rem',
    },
  },
  clientDisclaimerInfoWrapper: {
    paddingTop: 24,
  },
  clientDisclaimerInfo: {
    fontSize: 12,
    fontWeight: 300,
    color: theme.palette.text.primary,
    lineHeight: '20px',
  },
  redirectBody: {
    marginTop: theme.spacing(2),
  },
  headerText: ({ leavingAppDialogOverridesStyles }) => ({
    ..._get(leavingAppDialogOverridesStyles, 'headerText', {}),
  }),
}));

const RESET_PAYLOAD = {
  type: null,
  visible: false,
  title: null,
  url: null,
};

export default function LeavingAppDialog() {
  const theme = useClientTheme();
  const leavingAppDialogOverridesStyles = _get(theme, 'LeavingAppDialog');
  const customCloseIcon = _get(leavingAppDialogOverridesStyles, 'closeIcon');
  const classes = useStyles({ leavingAppDialogOverridesStyles });
  const dispatch = useDispatch();
  const location = useLocation();
  const history = useHistory();

  const {
    type,
    visible,
    title,
    url,
    organization,
    resourceType,
    articleURL,
    leavingModal,
    disclaimer,
    themes,
    topics,
    triggerElement,
  } = useSelector(makeSelectLeavingModal());

  const [siteCopy] = useSiteCopySelector(['leaving-app-modal']);
  const clientDetails = useSelector(makeSelectClientDetails());
  const showExternalRedirectPage =
    clientDetails?.metadata?.showExternalRedirectPage;

  const { pageHeader, callToAction, pageCopy } = siteCopy;
  const [embedParentPosition, setEmbedParentPosition] = useState({});
  const modalRef = useRef();
  const isEmbed = isEmbedded();

  // Get all custom labels for the current brand
  const customLabels = useClientCustomLabelsAll(siteCopy);
  const clientDisclaimerInfo = _get(customLabels, 'disclaimerInfo');

  useEffect(() => {
    let isMounted = true;

    if (!visible || !isEmbed) {
      return () => {
        isMounted = false;
      };
    }

    const fetchAndSetParentPosition = async () => {
      let parentPosition = await getParentPosition();

      // If triggerElement is available, calculate position relative to it
      if (triggerElement && isMounted) {
        const triggerRect = triggerElement.getBoundingClientRect();
        const modalHeight = 400; // Approximate modal height
        const viewportHeight = window.innerHeight;

        // Calculate top position: try to center modal near trigger element
        let calculatedTop =
          triggerRect.top + triggerRect.height / 2 - modalHeight / 2;

        // Ensure modal stays within viewport bounds
        if (calculatedTop < 20) {
          calculatedTop = 20; // Minimum top padding
        } else if (calculatedTop + modalHeight > viewportHeight - 20) {
          calculatedTop = viewportHeight - modalHeight - 20; // Maximum bottom padding
        }

        parentPosition = {
          ...parentPosition,
          top: calculatedTop,
        };
      }

      if (isMounted) {
        setEmbedParentPosition(parentPosition);
      }
    };

    fetchAndSetParentPosition();

    return () => {
      isMounted = false;
    };
  }, [visible, triggerElement, isEmbed]);

  if (!visible) return null;

  const isRedirect = type === 'redirect';
  const isEmbeddedNewTabRedirect = type === 'embedded_new_tab_redirect';

  const handleClose = (event, reason) => {
    if (isRedirect && reason && reason === 'backdropClick') return;

    dispatch(setLeavingModal(RESET_PAYLOAD));
  };

  const handleLinkClick = () => {
    if (type === 'redirect') {
      console.log('redirect');
    } else {
      if (showExternalRedirectPage) {
        history.replace('/redirect', {
          url,
          prevLocation: location.pathname,
          title,
          themes,
          topics,
        });
      } else {
        window.open(url, '_blank', 'noopener');
      }

      dispatch(setLeavingModal(RESET_PAYLOAD));

      const pageName = location.pathname.split('/')[1];
      const finalPageName = pageName ? `${pageName}Page` : 'homePage';

      Mixpanel.track('Leaving App Dialog - Clicked', {
        path: window.location.pathname,
        title,
        url,
        page: finalPageName,
        ...(themes && { themes }),
        ...(topics && { topics }),
      });
    }
  };

  let header = <>{pageHeader}</>;
  if (isRedirect) {
    header = _get(siteCopy, 'pageCopy.subdomainRedirectHeader');
  } else if (
    resourceType === 'pure-url' &&
    _get(siteCopy, 'pageCopy.externalUrlHeader')
  ) {
    header = <>{_get(siteCopy, 'pageCopy.externalUrlHeader')}</>;
  } else if (resourceType === 'article' && !leavingModal) {
    header = (
      <div>
        {_get(pageCopy, 'articleLabel')}
        <span className={classes.pageHeaderText}> {organization}</span>.{' '}
        {_get(pageCopy, 'headerLabel')}
      </div>
    );
  } else if (isEmbeddedNewTabRedirect) {
    header = _get(customLabels, 'embeddedRedirectHeader');
  }
  const redirectBody = _get(siteCopy, 'pageCopy.subdomainRedirectBody');
  const embedPositionTop = _get(embedParentPosition, 'top', null);
  const dialogProps = {
    onClose: handleClose,
    'aria-labelledby': 'remind-me-later-dialog',
    open: true,
    classes: {
      paper: classes.paper,
    },
  };

  if (embedPositionTop && isEmbed) {
    dialogProps.PaperProps = {
      style: {
        position: 'fixed',
        top: `${embedPositionTop}px`,
      },
    };
  }

  // pass custom close icon if exists in theme overrides
  if (customCloseIcon) {
    dialogProps.CloseIconComponent = () => customCloseIcon;
  }

  return (
    <ThemedDialog
      {...dialogProps}
      showCloseButton={!isRedirect}
      headerBoxClassName={classes.header}
      closeButtonClassName={classes.closeButton}
      ref={modalRef}
    >
      <DialogContent classes={{ root: classes.contentRoot }}>
        {title && leavingModal && (
          <Typography
            variant="overline"
            component="p"
            align="center"
            color="primary"
            gutterBottom
          >
            {title}
          </Typography>
        )}
        <Typography
          variant="h4"
          color="textPrimary"
          align="center"
          className={classes.headerText}
        >
          {header}
        </Typography>
        {isRedirect && (
          <Typography
            variant="body1"
            color="textPrimary"
            align="left"
            className={classes.redirectBody}
          >
            {redirectBody}
          </Typography>
        )}
        {disclaimer && (
          <div className={classes.disclaimerWrapper}>
            <RichTextDescription data={disclaimer} />
          </div>
        )}
        {isEmbeddedNewTabRedirect && (
          <EmbeddedRedirectContent customLabels={customLabels} />
        )}
        {!isRedirect && (
          <Box
            display="flex"
            flex="1"
            justifyContent="center"
            className={classes.buttonSpace}
          >
            <Button
              color="primary"
              role="link"
              variant="contained"
              aria-label={
                !showExternalRedirectPage && `Open ${url} link in a new tab`
              }
              className={classes.button}
              onClick={handleLinkClick}
            >
              {resourceType === 'article' && !leavingModal
                ? _get(
                    customLabels,
                    'articleButton',
                    _get(pageCopy, 'articleButton'),
                  )
                : callToAction}
            </Button>
            {resourceType === 'article' && !leavingModal && (
              <Link
                to={articleURL}
                color=""
                variant="contained"
                className={classes.articleButton}
                onClick={handleClose}
              >
                {_get(
                  customLabels,
                  'buttonLabel',
                  _get(pageCopy, 'buttonLabel'),
                )}
              </Link>
            )}
          </Box>
        )}
        {clientDisclaimerInfo && isEmbeddedNewTabRedirect && (
          <div className={classes.clientDisclaimerInfoWrapper}>
            <Typography className={classes.clientDisclaimerInfo} align="center">
              {clientDisclaimerInfo}
            </Typography>
          </div>
        )}
      </DialogContent>
    </ThemedDialog>
  );
}
